import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Pencil, AlertCircle, Loader2 } from 'lucide-react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useToast } from '@/hooks/use-toast';
import type { GanttFieldMapping } from '@/types/pm-types';
import type { Component } from '@/types';

interface EditGanttTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  ganttComponentId: string;
  taskId: string;
  taskData: Record<string, unknown>;
}

const DEFAULT_FIELD_MAPPING: GanttFieldMapping = {
  id: 'id',
  taskName: 'taskName',
  startDate: 'startDate',
  endDate: 'endDate',
  progress: 'progress',
  dependencies: 'dependencies',
  group: 'group',
  milestone: 'milestone',
  assignee: 'assignee',
};

interface ExistingTask {
  id: string;
  name: string;
}

export function EditGanttTaskModal({
  isOpen,
  onClose,
  ganttComponentId,
  taskId,
  taskData,
}: EditGanttTaskModalProps) {
  const editorStore = useEditorStore();
  const viewerStore = useViewerStore();
  const { toast } = useToast();

  const [fieldMapping, setFieldMapping] = useState<GanttFieldMapping>(DEFAULT_FIELD_MAPPING);
  const [existingTasks, setExistingTasks] = useState<ExistingTask[]>([]);
  const [existingGroups, setExistingGroups] = useState<string[]>([]);

  // Form state
  const [taskName, setTaskName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [progress, setProgress] = useState(0);
  const [group, setGroup] = useState('');
  const [milestone, setMilestone] = useState(false);
  const [selectedDeps, setSelectedDeps] = useState<string[]>([]);
  const [assignee, setAssignee] = useState('');
  const [parentId, setParentId] = useState('');
  const [color, setColor] = useState('');
  const [iteration, setIteration] = useState('');

  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form from existing task data when modal opens
  useEffect(() => {
    if (!isOpen || !ganttComponentId) return;

    let component: Component | undefined | null =
      editorStore.getComponent?.(ganttComponentId);
    if (!component) {
      component = viewerStore.getComponentById?.(ganttComponentId);
    }
    if (!component) return;

    const props = component.properties ?? {};
    const fm = (props.fieldMapping as GanttFieldMapping) ?? DEFAULT_FIELD_MAPPING;
    setFieldMapping(fm);

    const data: Record<string, unknown>[] =
      (Array.isArray(props.sampleData) ? props.sampleData : null) ??
      (Array.isArray(props.data) ? props.data : null) ??
      [];

    // Build task list for dependency/parent dropdowns (excluding current task)
    const tasks: ExistingTask[] = (data as Record<string, unknown>[]).map((row) => ({
      id: String(row[fm.id] ?? ''),
      name: String(row[fm.taskName] ?? ''),
    })).filter((t) => t.id && t.name && t.id !== taskId);
    setExistingTasks(tasks);

    // Build unique groups list
    if (fm.group) {
      const groups = [...new Set(
        (data as Record<string, unknown>[])
          .map((row) => String(row[fm.group!] ?? ''))
          .filter(Boolean)
      )];
      setExistingGroups(groups);
    } else {
      setExistingGroups([]);
    }

    // Pre-fill form from taskData
    setTaskName(String(taskData[fm.taskName] ?? ''));

    const rawStart = taskData[fm.startDate];
    setStartDate(formatDateValue(rawStart));

    if (fm.endDate) {
      const rawEnd = taskData[fm.endDate];
      setEndDate(formatDateValue(rawEnd));
    }

    setProgress(fm.progress ? Number(taskData[fm.progress] ?? 0) : 0);
    setGroup(fm.group ? String(taskData[fm.group] ?? '') : '');
    setMilestone(fm.milestone ? Boolean(taskData[fm.milestone]) : false);
    setAssignee(fm.assignee ? String(taskData[fm.assignee] ?? '') : '');
    setParentId(fm.parentId ? String(taskData[fm.parentId] ?? '') : '');
    setColor(fm.color ? String(taskData[fm.color] ?? '') : '');
    setIteration(fm.iteration ? String(taskData[fm.iteration] ?? '') : '');

    // Parse dependencies
    if (fm.dependencies) {
      const rawDeps = taskData[fm.dependencies];
      if (Array.isArray(rawDeps)) {
        setSelectedDeps(rawDeps.map(String));
      } else if (typeof rawDeps === 'string' && rawDeps.trim()) {
        setSelectedDeps(rawDeps.split(',').map((d) => d.trim()).filter(Boolean));
      } else {
        setSelectedDeps([]);
      }
    } else {
      setSelectedDeps([]);
    }

    setNameError(null);
    setDateError(null);
    setSubmitError(null);
    setIsSubmitting(false);
  }, [isOpen, ganttComponentId, taskId, taskData, editorStore, viewerStore]);

  const handleSubmit = () => {
    const trimmedName = taskName.trim();
    if (!trimmedName) {
      setNameError('Task name is required');
      return;
    }
    if (!startDate) {
      setDateError('Start date is required');
      return;
    }
    if (endDate && startDate && endDate < startDate) {
      setDateError('End date must be on or after start date');
      return;
    }

    setNameError(null);
    setDateError(null);
    setSubmitError(null);
    setIsSubmitting(true);

    // Build updated record
    const updates: Record<string, unknown> = {};
    updates[fieldMapping.taskName] = trimmedName;
    updates[fieldMapping.startDate] = startDate;

    if (fieldMapping.endDate) {
      updates[fieldMapping.endDate] = endDate || undefined;
    }
    if (fieldMapping.progress) {
      updates[fieldMapping.progress] = progress;
    }
    if (fieldMapping.group) {
      updates[fieldMapping.group] = group || undefined;
    }
    if (fieldMapping.milestone) {
      updates[fieldMapping.milestone] = milestone;
    }
    if (fieldMapping.dependencies) {
      updates[fieldMapping.dependencies] = selectedDeps.length > 0
        ? selectedDeps.join(',')
        : undefined;
    }
    if (fieldMapping.assignee) {
      updates[fieldMapping.assignee] = assignee || undefined;
    }
    if (fieldMapping.parentId) {
      updates[fieldMapping.parentId] = (parentId && parentId !== 'none') ? parentId : undefined;
    }
    if (fieldMapping.color) {
      updates[fieldMapping.color] = color || undefined;
    }
    if (fieldMapping.iteration) {
      updates[fieldMapping.iteration] = iteration || undefined;
    }

    const success = updateGanttTaskData(ganttComponentId, taskId, fieldMapping, updates);
    setIsSubmitting(false);

    if (success) {
      onClose();
      setTimeout(() => {
        toast({ title: 'Success', description: 'Task updated successfully' });
      }, 100);
    } else {
      setSubmitError('Failed to update task');
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleDep = (depTaskId: string) => {
    setSelectedDeps((prev) =>
      prev.includes(depTaskId)
        ? prev.filter((id) => id !== depTaskId)
        : [...prev, depTaskId]
    );
  };

  const inputClass = 'h-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary';
  const labelClass = 'text-sm font-medium text-gray-700';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="light" className="sm:max-w-lg p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Pencil className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Task
              </h3>
              <p className="text-sm text-gray-500">
                Update task details, dependencies, and group assignments.
              </p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-100" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Task Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-gantt-task-name" className={labelClass}>
              Task Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-gantt-task-name"
              value={taskName}
              onChange={(e) => {
                setTaskName(e.target.value);
                if (nameError) setNameError(null);
              }}
              onKeyDown={handleNameKeyDown}
              placeholder="e.g. API Development"
              className={`${inputClass} ${nameError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              autoFocus
              disabled={isSubmitting}
            />
            {nameError && (
              <div className="flex items-center gap-1.5 text-red-600">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs">{nameError}</p>
              </div>
            )}
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-gantt-start-date" className={labelClass}>
                Start Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-gantt-start-date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (dateError) setDateError(null);
                }}
                className={`${inputClass} ${dateError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                disabled={isSubmitting}
              />
            </div>

            {fieldMapping.endDate && (
              <div className="space-y-2">
                <Label htmlFor="edit-gantt-end-date" className={labelClass}>
                  End Date <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <Input
                  id="edit-gantt-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    if (dateError) setDateError(null);
                  }}
                  className={`${inputClass} ${dateError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>
          {dateError && (
            <div className="flex items-center gap-1.5 text-red-600 -mt-3">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <p className="text-xs">{dateError}</p>
            </div>
          )}

          {/* Progress + Assignee */}
          {(fieldMapping.progress || fieldMapping.assignee) && (
            <div className="grid grid-cols-2 gap-4">
              {fieldMapping.progress && (
                <div className="space-y-2">
                  <Label htmlFor="edit-gantt-progress" className={labelClass}>
                    Progress (%)
                  </Label>
                  <Input
                    id="edit-gantt-progress"
                    type="number"
                    min={0}
                    max={100}
                    value={progress}
                    onChange={(e) => setProgress(Number(e.target.value) || 0)}
                    placeholder="0"
                    className={inputClass}
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {fieldMapping.assignee && (
                <div className="space-y-2">
                  <Label htmlFor="edit-gantt-assignee" className={labelClass}>
                    Assignee
                  </Label>
                  <Input
                    id="edit-gantt-assignee"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    placeholder="e.g. Alice"
                    className={inputClass}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          )}

          {/* Group */}
          {fieldMapping.group && (
            <div className="space-y-2">
              <Label htmlFor="edit-gantt-group" className={labelClass}>
                Group
              </Label>
              {existingGroups.length > 0 ? (
                <Select
                  value={group}
                  onValueChange={setGroup}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="edit-gantt-group" className={inputClass}>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingGroups.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="edit-gantt-group"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  placeholder="e.g. Development"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              )}
            </div>
          )}

          {/* Parent Task */}
          {fieldMapping.parentId && existingTasks.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="edit-gantt-parent" className={labelClass}>
                Parent Task
              </Label>
              <Select
                value={parentId || 'none'}
                onValueChange={setParentId}
                disabled={isSubmitting}
              >
                <SelectTrigger id="edit-gantt-parent" className={inputClass}>
                  <SelectValue placeholder="None (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {existingTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Dependencies */}
          {fieldMapping.dependencies && existingTasks.length > 0 && (
            <div className="space-y-2">
              <Label className={labelClass}>
                Dependencies
              </Label>
              <div className="max-h-32 overflow-y-auto rounded-md border border-gray-200 bg-white p-2 space-y-0.5">
                {existingTasks.map((task) => (
                  <label
                    key={task.id}
                    className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded-md transition-colors"
                  >
                    <Checkbox
                      checked={selectedDeps.includes(task.id)}
                      onCheckedChange={() => toggleDep(task.id)}
                      disabled={isSubmitting}
                    />
                    {task.name}
                  </label>
                ))}
              </div>
              {selectedDeps.length > 0 && (
                <p className="text-xs text-gray-400">
                  {selectedDeps.length} dependency{selectedDeps.length > 1 ? 'ies' : ''} selected
                </p>
              )}
            </div>
          )}

          {/* Milestone + Color */}
          {(fieldMapping.milestone || fieldMapping.color) && (
            <div className="flex items-end gap-6">
              {fieldMapping.milestone && (
                <div className="flex items-center gap-2.5 py-2">
                  <Checkbox
                    id="edit-gantt-milestone"
                    checked={milestone}
                    onCheckedChange={(checked) => setMilestone(!!checked)}
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="edit-gantt-milestone" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Milestone
                  </Label>
                </div>
              )}

              {fieldMapping.color && (
                <div className="space-y-2">
                  <Label htmlFor="edit-gantt-color" className={labelClass}>
                    Color
                  </Label>
                  <Input
                    id="edit-gantt-color"
                    type="color"
                    value={color || '#3B82F6'}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-10 w-16 p-1 border-gray-200 cursor-pointer"
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          )}

          {/* Iteration */}
          {fieldMapping.iteration && (
            <div className="space-y-2">
              <Label htmlFor="edit-gantt-iteration" className={labelClass}>
                Iteration
              </Label>
              <Input
                id="edit-gantt-iteration"
                value={iteration}
                onChange={(e) => setIteration(e.target.value)}
                placeholder="e.g. Sprint 1"
                className={inputClass}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Submit Error Banner */}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="sm:w-auto w-full border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !taskName.trim() || !startDate}
            className="sm:w-auto w-full min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Format a date value (Date, string, etc.) to YYYY-MM-DD for input[type=date] */
function formatDateValue(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date) return raw.toISOString().split('T')[0];
  const str = String(raw);
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // Try parsing
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return '';
}

/**
 * Updates an existing task record in the Gantt chart's data in the store.
 */
function updateGanttTaskData(
  componentId: string,
  taskId: string,
  fieldMapping: GanttFieldMapping,
  updates: Record<string, unknown>,
): boolean {
  try {
    const editorState = useEditorStore.getState();
    let component: Component | undefined | null = editorState.getComponent?.(componentId);
    let updateComponent = editorState.updateComponent;

    if (!component) {
      const viewerState = useViewerStore.getState();
      component = viewerState.getComponentById?.(componentId);
      updateComponent = viewerState.updateComponent;
    }

    if (!component) {
      console.error(`[EditGanttTask] Component "${componentId}" not found`);
      return false;
    }

    const props = component.properties ?? {};

    const currentData: Record<string, unknown>[] =
      (Array.isArray(props.sampleData) ? (props.sampleData as Record<string, unknown>[]) : null) ??
      (Array.isArray(props.data) ? (props.data as Record<string, unknown>[]) : null) ??
      [];

    // Find and update the matching task record
    const updatedData = currentData.map((row) => {
      if (String(row[fieldMapping.id]) === taskId) {
        return { ...row, ...updates };
      }
      return row;
    });

    updateComponent(componentId, {
      properties: {
        ...props,
        sampleData: updatedData,
      },
    });

    return true;
  } catch (error) {
    console.error('[EditGanttTask] Error updating task:', error);
    return false;
  }
}
